/**
 * OperatingModeWidget — Betriebsmodus-Schalter (01.06.) fuer das Leadmanagement.
 *
 * Drei Modi (DB-gestuetzt, zur Laufzeit umschaltbar):
 *   - Aus  : Not-Stopp — kein Mailversand, kein Auto-Move
 *   - Test : Versand NUR an Allowlist; Allowlist-Leads (= Test-Leads) zyklen mit
 *            testTimerMinutes; echte Kunden bleiben geparkt
 *   - Live : Versand an alle, 7-Tage-Logik
 *
 * Wechsel auf Live verlangt eine Bestaetigung ("LIVE" tippen). Jeder mit
 * Programm-Zugriff darf umschalten; der Server protokolliert wer/wann.
 *
 * CI-Farben: #E9FF57 / #1A1A1A / #DAD9D9. NIE weisse Schrift auf Gelb.
 */
import { useEffect, useState } from 'react';
import { leadsApi } from '../../services/leadsApi';
import type { OperatingMode, ProgrammeSettings } from '../../types';

const MODES: Array<{ key: OperatingMode; label: string; hint: string }> = [
  { key: 'off', label: 'Aus', hint: 'Not-Stopp: keine Mails, kein Auto-Move' },
  { key: 'test', label: 'Test', hint: 'Nur Allowlist; Test-Leads zyklen schnell' },
  { key: 'live', label: 'Live', hint: 'Versand an alle Vereine, 7-Tage-Logik' },
];

function modeButtonClass(active: boolean, mode: OperatingMode): string {
  if (!active) return 'bg-white text-[#1A1A1A] border-[#DAD9D9] hover:bg-gray-50';
  if (mode === 'test') return 'bg-[#E9FF57] text-[#1A1A1A] border-[#E9FF57]';
  if (mode === 'live') return 'bg-green-600 text-white border-green-600';
  return 'bg-[#1A1A1A] text-white border-[#1A1A1A]'; // off
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function OperatingModeWidget() {
  const [settings, setSettings] = useState<ProgrammeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Live-Bestaetigungsdialog
  const [confirmLive, setConfirmLive] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  // Allowlist-Editor
  const [newEmail, setNewEmail] = useState('');
  // Timer-Feld (lokal editierbar, Save on blur/button)
  const [timerInput, setTimerInput] = useState('5');
  // Test-Lead anlegen
  const [testLeadEmail, setTestLeadEmail] = useState('');
  const [creatingLead, setCreatingLead] = useState(false);

  useEffect(() => {
    let active = true;
    leadsApi.settings
      .get()
      .then((s) => {
        if (!active) return;
        setSettings(s);
        setTimerInput(String(s.testTimerMinutes));
        setTestLeadEmail(s.allowlist[0] ?? '');
        setLoading(false);
      })
      .catch((e: any) => {
        if (!active) return;
        setError(e?.message ?? 'Einstellungen konnten nicht geladen werden');
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  async function persist(patch: Partial<Pick<ProgrammeSettings, 'mode' | 'testTimerMinutes' | 'allowlist'>>, successMsg: string) {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await leadsApi.settings.update(patch);
      setSettings(updated);
      setTimerInput(String(updated.testTimerMinutes));
      if (!updated.allowlist.includes(testLeadEmail)) setTestLeadEmail(updated.allowlist[0] ?? '');
      setNotice(successMsg);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? e?.message ?? 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  function handleModeClick(mode: OperatingMode) {
    if (!settings || mode === settings.mode || saving) return;
    if (mode === 'live') {
      setConfirmText('');
      setConfirmLive(true);
      return;
    }
    persist({ mode }, `Modus auf "${mode === 'off' ? 'Aus' : 'Test'}" gesetzt`);
  }

  function confirmGoLive() {
    if (confirmText.trim().toUpperCase() !== 'LIVE') return;
    setConfirmLive(false);
    persist({ mode: 'live' }, 'Modus auf "Live" gesetzt — Versand an alle Vereine aktiv');
  }

  function addEmail() {
    if (!settings) return;
    const e = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) {
      setError(`Ungueltige E-Mail: ${newEmail}`);
      return;
    }
    if (settings.allowlist.includes(e)) {
      setNewEmail('');
      return;
    }
    persist({ allowlist: [...settings.allowlist, e] }, `${e} zur Allowlist hinzugefuegt`);
    setNewEmail('');
  }

  function removeEmail(email: string) {
    if (!settings) return;
    persist({ allowlist: settings.allowlist.filter((x) => x !== email) }, `${email} entfernt`);
  }

  function saveTimer() {
    const n = Number(timerInput);
    if (!Number.isFinite(n) || n < 1) {
      setError('Test-Timer muss mindestens 1 Minute sein');
      return;
    }
    persist({ testTimerMinutes: n }, `Test-Timer auf ${n} Min gesetzt`);
  }

  async function createTestLead() {
    if (!testLeadEmail) return;
    setCreatingLead(true);
    setError(null);
    setNotice(null);
    try {
      const stamp = new Date().toISOString().slice(5, 16).replace('T', ' ');
      await leadsApi.createLead({
        vereinName: `Test-Lead ${stamp}`,
        vereinContactPerson: 'Test',
        vereinEmail: testLeadEmail,
        tags: ['test-lead'],
      } as any);
      setNotice(`Test-Lead fuer ${testLeadEmail} angelegt — startet im Board (lead_form)`);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? e?.message ?? 'Test-Lead konnte nicht angelegt werden');
    } finally {
      setCreatingLead(false);
    }
  }

  if (loading) {
    return (
      <div className="mb-6 p-4 border border-[#DAD9D9] rounded-lg bg-white">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">Betriebsmodus</h3>
        <p className="text-sm text-gray-500">Wird geladen ...</p>
      </div>
    );
  }
  if (!settings) {
    return (
      <div className="mb-6 p-4 border border-red-200 rounded-lg bg-red-50">
        <h3 className="text-sm font-semibold text-red-700 mb-2">Betriebsmodus</h3>
        <p className="text-sm text-red-700">{error ?? 'Einstellungen nicht verfuegbar'}</p>
      </div>
    );
  }

  return (
    <div className="mb-6 p-4 border border-[#DAD9D9] rounded-lg bg-white">
      <h3 className="text-sm font-semibold text-[#1A1A1A] mb-3">Betriebsmodus</h3>

      {/* Modus-Schalter */}
      <div className="flex gap-2">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => handleModeClick(m.key)}
            disabled={saving}
            title={m.hint}
            className={`flex-1 px-3 py-2 rounded border text-sm font-medium transition-colors disabled:opacity-60 ${modeButtonClass(
              settings.mode === m.key,
              m.key,
            )}`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-gray-600">
        {MODES.find((m) => m.key === settings.mode)?.hint}
      </p>

      {notice && <p className="mt-2 text-sm text-green-700">{notice}</p>}
      {error && <p className="mt-2 text-sm text-red-700">{error}</p>}

      {/* Live-Bestaetigung */}
      {confirmLive && (
        <div className="mt-3 p-3 border border-green-300 rounded bg-green-50">
          <p className="text-sm text-[#1A1A1A] font-medium">
            Live scharfschalten? Dann gehen Mails an <strong>alle</strong> Vereine raus.
          </p>
          <p className="mt-1 text-xs text-gray-600">Tippe <strong>LIVE</strong> zur Bestaetigung:</p>
          <div className="mt-2 flex gap-2">
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="LIVE"
              className="flex-1 px-2 py-1 border border-[#DAD9D9] rounded text-sm"
            />
            <button
              type="button"
              onClick={confirmGoLive}
              disabled={confirmText.trim().toUpperCase() !== 'LIVE'}
              className="px-3 py-1 rounded bg-green-600 text-white text-sm font-medium disabled:opacity-50"
            >
              Live aktivieren
            </button>
            <button
              type="button"
              onClick={() => setConfirmLive(false)}
              className="px-3 py-1 rounded border border-[#DAD9D9] text-sm text-[#1A1A1A]"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Test-Timer (nur im Test-Modus relevant, immer editierbar) */}
      <div className="mt-4 pt-3 border-t border-[#DAD9D9]">
        <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
          Test-Timer (Minuten pro Stufe)
        </label>
        <div className="flex gap-2 items-center">
          <input
            type="number"
            min={1}
            value={timerInput}
            onChange={(e) => setTimerInput(e.target.value)}
            className="w-24 px-2 py-1 border border-[#DAD9D9] rounded text-sm"
          />
          <button
            type="button"
            onClick={saveTimer}
            disabled={saving || timerInput === String(settings.testTimerMinutes)}
            className="px-3 py-1 rounded bg-[#1A1A1A] text-white text-sm disabled:opacity-50"
          >
            Speichern
          </button>
          <span className="text-xs text-gray-500">aktuell: {settings.testTimerMinutes} Min</span>
        </div>
      </div>

      {/* Allowlist-Editor */}
      <div className="mt-4 pt-3 border-t border-[#DAD9D9]">
        <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
          Allowlist ({settings.allowlist.length}) — bekommt im Test-Modus Mails &amp; gilt als Test-Lead
        </label>
        <ul className="space-y-1 mb-2">
          {settings.allowlist.length === 0 && (
            <li className="text-xs text-gray-500">Leer — im Test-Modus geht nichts raus.</li>
          )}
          {settings.allowlist.map((email) => (
            <li key={email} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-sm">
              <span className="text-[#1A1A1A]">{email}</span>
              <button
                type="button"
                onClick={() => removeEmail(email)}
                disabled={saving}
                className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                aria-label={`${email} entfernen`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addEmail()}
            placeholder="email@anstoss24.de"
            className="flex-1 px-2 py-1 border border-[#DAD9D9] rounded text-sm"
          />
          <button
            type="button"
            onClick={addEmail}
            disabled={saving || newEmail.trim() === ''}
            className="px-3 py-1 rounded bg-[#E9FF57] text-[#1A1A1A] text-sm font-medium disabled:opacity-50"
          >
            Hinzufuegen
          </button>
        </div>
      </div>

      {/* Test-Lead anlegen */}
      <div className="mt-4 pt-3 border-t border-[#DAD9D9]">
        <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
          Test-Lead anlegen (Empfaenger aus Allowlist — durchlaeuft im Test-Modus die Stufen schnell)
        </label>
        <div className="flex gap-2">
          <select
            value={testLeadEmail}
            onChange={(e) => setTestLeadEmail(e.target.value)}
            disabled={settings.allowlist.length === 0}
            className="flex-1 px-2 py-1 border border-[#DAD9D9] rounded text-sm bg-white"
          >
            {settings.allowlist.length === 0 && <option value="">(Allowlist leer)</option>}
            {settings.allowlist.map((email) => (
              <option key={email} value={email}>{email}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={createTestLead}
            disabled={creatingLead || !testLeadEmail}
            className="px-3 py-1 rounded bg-[#1A1A1A] text-white text-sm disabled:opacity-50"
          >
            {creatingLead ? 'Lege an ...' : 'Test-Lead anlegen'}
          </button>
        </div>
        {settings.mode !== 'test' && (
          <p className="mt-1 text-xs text-gray-500">
            Hinweis: schnelles Zyklen passiert nur im Test-Modus.
          </p>
        )}
      </div>
    </div>
  );
}
